import axios from "axios";
import { AUTH_JWT_KEY } from "./localStorage";

export const setAuthToken = (token: string) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    axios.defaults.headers.common["Authorization"] = undefined;
  }
};

/**
 * Fallback that guarantees a stored session's JWT rides on EVERY request,
 * regardless of React effect ordering.
 *
 * setAuthToken() above is only called from App.tsx's mount effect - but
 * Router.tsx (a child of App) dispatches fetchSubscription in its own [jwt]
 * effect, and React runs child effects before parent effects. On every load
 * with a stored session, the first getSubscriptionState therefore left
 * without an Authorization header and 403'd (dashboard's "problème avec le
 * serveur" banner; WIXAR preprod incident of 2026-08-05).
 *
 * Installed from index.tsx at module scope - before React renders anything -
 * so no dispatch can outrun it. It only fills ABSENCE: an Authorization set
 * explicitly (setAuthToken's default, or a per-request header) always wins.
 * Logout is safe: authSlice clears localStorage, so there is nothing to
 * attach afterwards. Guarded by isTokenExpired to mirror App.tsx's own rule,
 * with malformed tokens ignored rather than thrown on.
 */
export const installAuthHeaderFallback = () => {
  axios.interceptors.request.use((config) => {
    if (!config.headers?.Authorization) {
      const jwt = localStorage.getItem(AUTH_JWT_KEY);
      if (jwt) {
        try {
          if (!isTokenExpired(jwt)) {
            config.headers.Authorization = `Bearer ${jwt}`;
          }
        } catch {
          // Malformed token in storage: send the request untouched rather
          // than crash the interceptor chain.
        }
      }
    }
    return config;
  });
};

export const setAxiosResponseHandler = (alertCb: any) => {
  axios.interceptors?.response.use(
    (response: any) => {
      // Check if the response has a 'data' key at the top-level object
      // and unwrap it so that you don't have to access response.data.data everywhere
      if (response.data && typeof response.data === "object" && response.data.data !== undefined) {
        // console.log(
        //   "setAxiosResponseHandler - strapi-v4 structure detected: unwrapping 'data.data' from response: ",
        //   response.data.data,
        // );
        // Modify the response to unwrap the data key
        return { ...response, data: response.data.data };
      }
      // console.log("setAxiosResponseHandler - returning response 'as is': ", response.data);
      return response;
    },
    (error) => {
      console.error("setAxiosResponseHandler Axios interceptor error: ", error);
      return Promise.reject(alertCb(error?.response));
    },
  );
};

export const setAxiosRequestHandler = () => {
  axios.interceptors.request.use(
    (config) => {
      // Check if the method is POST or PUT
      if (config.method === "post" || config.method === "put") {
        if (config?.data) {
          const hasDataKey = Object.prototype.hasOwnProperty.call(config?.data, "data");

          // Check if the payload is an object and not already wrapped with a "data" key
          if (config?.data && typeof config?.data === "object" && !hasDataKey) {
            config.data = { data: config?.data };
          }
        }
      }

      return config;
    },
    (error) => {
      console.error("setAxiosRequestHandler Axios interceptor error: ", error);

      // Do something with request error
      return Promise.reject(error);
    },
  );
};

export const isTokenExpired = (jwt: string): boolean => {
  const decodedToken: any = JSON.parse(window.atob(jwt.split(".")[1]));
  const currentDate = new Date();

  // JWT exp is in seconds
  if (decodedToken.exp * 1000 < currentDate.getTime()) {
    return true;
  }
  return false;
};
